// Client-side PDF generation (no server build → no proxy/gateway timeout on
// large, photo-heavy reports). Pulls the report JSON, fetches each photo through
// the cached same-origin image proxy, and assembles a clean, client-facing
// Before/After PDF in the browser. Branding uses the owner's company logo +
// details from account settings, falling back to the Send-Proof mark.
import { jsPDF } from "jspdf";
import { api } from "@/lib/api";
import { API } from "@/lib/offline/net";

const PROXY = (path, w = 1200, q = 72) => `${API}/img?path=${encodeURIComponent(path)}&w=${w}&q=${q}`;
const DEFAULT_LOGO = "/logo-mark.png";

// palette
const INK = [17, 24, 39];
const SUB = [100, 116, 139];
const FAINT = [148, 163, 184];
const EM = [16, 185, 129];
const EM_DK = [15, 118, 80];
const AMBER = [202, 110, 10];
const BG = [248, 250, 252];
const LINE = [226, 232, 240];

function S(t) {
  return (t || "")
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\u00b7/g, "-")
    .replace(/\u2026/g, "...");
}

async function blobToDataUrl(blob) {
  return await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = rej;
    fr.readAsDataURL(blob);
  });
}
async function toDataUrl(url, opts = {}) {
  const r = await fetch(url, opts);
  if (!r.ok) throw new Error("img " + r.status);
  return await blobToDataUrl(await r.blob());
}
function sizeOf(dataUrl) {
  return new Promise((res) => {
    const im = new Image();
    im.onload = () => res({ w: im.naturalWidth || 4, h: im.naturalHeight || 3 });
    im.onerror = () => res({ w: 4, h: 3 });
    im.src = dataUrl;
  });
}
async function loadImage(url, opts) {
  const d = await toDataUrl(url, opts);
  return { d, ...(await sizeOf(d)) };
}
async function pool(list, n, fn) {
  let i = 0;
  await Promise.all(Array.from({ length: Math.min(n, list.length) }, async () => {
    while (i < list.length) { const idx = i++; await fn(list[idx], idx); }
  }));
}

const isDone = (it) => ["done", "na", "sub_done"].includes(it.status);
const beforeOf = (it) => (it.photos || []).find((p) => (p.kind || "reference") !== "proof") || null;
const afterOf = (it) => {
  const a = (it.photos || []).filter((p) => p.kind === "proof");
  return a.length ? a[a.length - 1] : null;
};
const pathOf = (p) => p && (p.thumbnail_cloud_path || p.cloud_path || p.thumb_url || p.url);
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
};

export async function buildReportPdfBlob(reportId) {
  const [{ data: rep }, me] = await Promise.all([
    api.raw.get(`/reports/${reportId}`),
    api.raw.get("/auth/me").then((r) => r.data).catch(() => null),
  ]);
  const report = rep.report || {};
  const job = rep.job || {};
  const items = rep.items || [];
  const stats = rep.stats || {};

  const brand = {
    company: (me?.company_name || "").trim(),
    address: (me?.company_address || "").trim(),
    email: (me?.email || "").trim(),
    logo: me?.company_logo_url || "",
  };
  const hasUserLogo = !!brand.logo;

  // preload photos (1 before + 1 after per item) via the cached proxy
  const cache = {};
  const paths = new Set();
  for (const it of items) for (const p of [beforeOf(it), afterOf(it)]) { const pt = pathOf(p); if (pt) paths.add(pt); }
  await pool([...paths], 6, async (pt) => { try { cache[pt] = await loadImage(PROXY(pt)); } catch { /* skip */ } });
  const imgFor = (p) => { const pt = pathOf(p); return pt ? cache[pt] : null; };

  // preload the header logo: user's (via proxy) → else Send-Proof mark (same-origin)
  let logo = null;
  if (hasUserLogo) { try { logo = await loadImage(PROXY(brand.logo, 400, 90)); } catch { logo = null; } }
  if (!logo) { try { logo = await loadImage(DEFAULT_LOGO); } catch { logo = null; } }

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, H = 297, M = 14, CW = W - M * 2;
  const total = items.length;
  const done = items.filter(isDone).length;
  const awaiting = total - done;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const trade = report.dispatched_trade || report.title || "Punch list";
  const host = (window.location.host || "send-proof.com").replace(/\/$/, "");
  const shortLink = stats.token ? `${host}/r/${stats.token}` : "";

  // Standard document metadata — Gmail/preview viewers reject PDFs missing these.
  doc.setProperties({
    title: `${report.title || trade}${job.name ? " — " + job.name : ""}`,
    subject: "Punch list / proof-of-work report",
    author: brand.company || "Send-Proof",
    creator: "Send-Proof (send-proof.com)",
    keywords: "punch list, proof of work, Send-Proof",
  });

  const font = (style = "normal", size = 10, color = INK) => doc.setFont("helvetica", style).setFontSize(size).setTextColor(...color);

  // ---------- Cover letterhead ----------
  let y = 15;
  const logoH = 15;
  let logoW = 0;
  if (logo) {
    logoW = Math.min(34, logoH * (logo.w / Math.max(1, logo.h)));
    try { doc.addImage(logo.d, "PNG", M, y, logoW, logoH, undefined, "FAST"); } catch { logoW = 0; }
  }
  const tx = M + (logoW ? logoW + 5 : 0);
  if (hasUserLogo) {
    font("bold", 15, INK); doc.text(S(brand.company || "Field Report"), tx, y + 5.5);
    font("normal", 9, SUB);
    let ly = y + 10.5;
    if (brand.address) { doc.text(S(brand.address), tx, ly); ly += 4.3; }
    if (brand.email) { doc.text(S(brand.email), tx, ly); }
  } else {
    font("bold", 16, EM_DK); doc.text("Send-Proof", tx, y + 6);
    font("normal", 9, SUB); doc.text("Proof it got done.", tx, y + 11);
    doc.text("send-proof.com", tx, y + 15);
  }
  y += logoH + 5;
  doc.setDrawColor(...EM).setLineWidth(0.8).line(M, y, W - M, y);
  doc.setLineWidth(0.2); y += 9;

  // title block
  font("bold", 7.5, EM_DK);
  doc.text("F I E L D   R E P O R T", M, y); y += 6;
  font("bold", 21, INK);
  const titleLines = doc.splitTextToSize(S(report.title || trade), CW);
  doc.text(titleLines, M, y); y += titleLines.length * 8 + 1;
  if (job.name) { font("normal", 12, SUB); doc.text(S(job.name), M, y); y += 7; }
  else y += 2;

  // details panel
  const panelH = 24;
  doc.setFillColor(...BG).setDrawColor(...LINE).roundedRect(M, y, CW, panelH, 2.5, 2.5, "FD");
  const col = (cx, label, value) => {
    font("bold", 7, FAINT); doc.text(label.toUpperCase(), cx, y + 7);
    font("bold", 11, INK);
    const v = doc.splitTextToSize(S(value || "-"), CW / 2 - 14);
    doc.text(v.slice(0, 1), cx, y + 12.5);
  };
  const cxL = M + 6, cxR = M + CW / 2 + 4;
  col(cxL, "Project", job.name);
  col(cxR, "Location", job.location || "-");
  font("bold", 7, FAINT); doc.text("DATE", cxL, y + 18);
  font("bold", 11, INK); doc.text(S(fmtDate(report.created_at) || "-"), cxL, y + 23.5);
  font("bold", 7, FAINT); doc.text("ITEMS", cxR, y + 18);
  font("bold", 11, INK); doc.text(`${total}`, cxR, y + 23.5);
  y += panelH + 9;

  // progress
  font("bold", 11, INK); doc.text("Progress", M, y);
  font("bold", 11, EM_DK); doc.text(`${pct}%`, W - M, y, { align: "right" });
  y += 3;
  const barH = 3.4;
  doc.setFillColor(...LINE).roundedRect(M, y, CW, barH, 1.7, 1.7, "F");
  if (pct > 0) { doc.setFillColor(...EM); doc.rect(M, y, Math.max(1.5, CW * pct / 100), barH, "F"); }
  y += barH + 5;
  font("normal", 9.5, SUB);
  doc.text(S(`${done} complete  -  ${awaiting} awaiting proof  -  ${total} total`), M, y);
  y += 8;

  // online callout
  if (shortLink) {
    const bh = 20;
    doc.setFillColor(236, 253, 245).setDrawColor(...EM).setLineWidth(0.4).roundedRect(M, y, CW, bh, 2.5, 2.5, "FD");
    doc.setLineWidth(0.2);
    font("bold", 8.5, EM_DK); doc.text("MARK ITEMS DONE ONLINE  -  NO APP NEEDED", M + CW / 2, y + 7.5, { align: "center" });
    font("bold", 11.5, INK); doc.text(S(shortLink), M + CW / 2, y + 14.5, { align: "center" });
    y += bh + 9;
  }

  // checklist
  font("bold", 13, INK); doc.text("Task Checklist", M, y); y += 2.5;
  doc.setDrawColor(...LINE).setLineWidth(0.3).line(M, y, W - M, y); doc.setLineWidth(0.2); y += 6;
  const checkbox = (x, cy, dn) => {
    doc.setLineWidth(0.4);
    if (dn) {
      doc.setFillColor(...EM).setDrawColor(...EM).roundedRect(x, cy, 4.6, 4.6, 1, 1, "FD");
      doc.setDrawColor(255, 255, 255).setLineWidth(0.7);
      doc.line(x + 1.0, cy + 2.5, x + 1.9, cy + 3.4); doc.line(x + 1.9, cy + 3.4, x + 3.7, cy + 1.1);
    } else {
      doc.setFillColor(255, 255, 255).setDrawColor(170, 180, 195).roundedRect(x, cy, 4.6, 4.6, 1, 1, "S");
    }
    doc.setLineWidth(0.2);
  };
  items.forEach((it, idx) => {
    if (y > 276) { doc.addPage(); y = 20; }
    const n = idx + 1, dn = isDone(it);
    font("normal", 9, FAINT); doc.text(`${n}.`, M, y + 3.4);
    checkbox(M + 9, y + 0.7, dn);
    let title = S(it.title || `Item ${n}`);
    font(dn ? "normal" : "normal", 10.5, dn ? FAINT : INK);
    while (doc.getTextWidth(title) > 150 && title.length > 4) title = title.slice(0, -2);
    doc.text(title, M + 17, y + 3.4);
    if (dn) { const tw = doc.getTextWidth(title); doc.setDrawColor(...FAINT).setLineWidth(0.4).line(M + 17, y + 2.5, M + 17 + tw, y + 2.5).setLineWidth(0.2); }
    y += 6.2;
  });

  // ---------- Before / After photo pages ----------
  const runHeader = () => {
    let hy = 11;
    if (logo) { try { doc.addImage(logo.d, "PNG", M, hy - 4, 6.5, 6.5, undefined, "FAST"); } catch { /* ignore */ } }
    font("bold", 9, INK);
    const ht = doc.splitTextToSize(S(hasUserLogo ? (brand.company || report.title) : (report.title || trade)), 120);
    doc.text(ht.slice(0, 1), M + (logo ? 9 : 0), hy);
    font("normal", 8, FAINT); doc.text(S(trade), W - M, hy, { align: "right" });
    hy += 3; doc.setDrawColor(...LINE).setLineWidth(0.3).line(M, hy, W - M, hy); doc.setLineWidth(0.2);
    return hy + 7;
  };
  const BOX_H = 95, GAP = 7, COLW = (CW - GAP) / 2;
  items.forEach((it, idx) => {
    const n = idx + 1;
    const before = beforeOf(it), after = afterOf(it);
    if (!before && !after) return;
    doc.addPage();
    let py = runHeader();

    font("bold", 12.5, INK);
    const tl = doc.splitTextToSize(S(`${n}.  ${it.title || `Item ${n}`}`), CW);
    doc.text(tl, M, py); py += tl.length * 6 + 2;

    const complete = !!after || ["done", "sub_done"].includes(it.status);
    const tag = complete ? "COMPLETE - PROOF ATTACHED" : "AWAITING PROOF";
    const tagColor = complete ? EM_DK : AMBER;
    const tagBg = complete ? [236, 253, 245] : [254, 243, 221];
    font("bold", 8, tagColor);
    const tw = doc.getTextWidth(tag) + 7;
    doc.setFillColor(...tagBg).roundedRect(M, py - 3.6, tw, 6, 3, 3, "F");
    doc.text(tag, M + 3.5, py); py += 7;

    const top = py;
    const cell = (x, label, isAfter, photo) => {
      font("bold", 8.5, isAfter ? EM_DK : SUB);
      doc.text(label, x, top);
      const iy = top + 5;
      doc.setDrawColor(...LINE).setFillColor(250, 251, 253).roundedRect(x, iy, COLW, BOX_H, 2, 2, "FD");
      const im = imgFor(photo);
      if (im) {
        const ratio = im.h / Math.max(1, im.w);
        const pad = 1.5;
        let dispW = COLW - pad * 2, dispH = dispW * ratio;
        const maxH = BOX_H - pad * 2;
        if (dispH > maxH) { dispH = maxH; dispW = dispH / ratio; }
        try { doc.addImage(im.d, "JPEG", x + (COLW - dispW) / 2, iy + (BOX_H - dispH) / 2, dispW, dispH, undefined, "FAST"); return; } catch { /* fall through */ }
      }
      font("normal", 9, FAINT);
      doc.text(isAfter ? "Awaiting proof" : "No before photo", x + COLW / 2, iy + BOX_H / 2, { align: "center" });
    };
    cell(M, "BEFORE", false, before);
    cell(M + COLW + GAP, "AFTER", true, after);
    py = top + 5 + BOX_H + 7;

    const line = (txt, color = SUB, size = 10, bold = false) => {
      if (!txt) return;
      font(bold ? "bold" : "normal", size, color);
      const ls = doc.splitTextToSize(S(txt), CW);
      doc.text(ls, M, py); py += ls.length * 4.8 + 1;
    };
    line((after && after.caption) || (before && before.caption) || "", INK, 10.5, true);
    line(it.notes, SUB, 10);
    const whenIso = (after && (after.created_at)) || it.submitted_at || it.created_at;
    const metaBits = [job.location, fmtDate(whenIso)].filter(Boolean).join("  -  ");
    line(metaBits, FAINT, 8.5);
  });

  // ---------- footer on every page ----------
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...LINE).setLineWidth(0.3).line(M, H - 12, W - M, H - 12); doc.setLineWidth(0.2);
    font("normal", 8, FAINT);
    if (brand.company) doc.text(S(brand.company), M, H - 7.5);
    doc.text("Made with Send-Proof  -  send-proof.com", W / 2, H - 7.5, { align: "center" });
    doc.text(`Page ${i} of ${pages}`, W - M, H - 7.5, { align: "right" });
  }

  return doc.output("blob");
}
